import { api } from './client';

export type IncomingDocumentStatus = 'NEW' | 'ACCEPTED' | 'REJECTED';

export const INCOMING_STATUS_LABELS: Record<IncomingDocumentStatus, string> = {
  NEW: 'На рассмотрении',
  ACCEPTED: 'Подписан',
  REJECTED: 'Отклонён',
};

export const INCOMING_STATUS_COLORS: Record<IncomingDocumentStatus, string> = {
  NEW: 'blue',
  ACCEPTED: 'green',
  REJECTED: 'red',
};

export interface IncomingUpdLine {
  id: string;
  lineNumber: number;
  nomenclatureId: string | null;
  name: string;
  unitSymbol: string;
  quantity: string;
  price: string;
  vatRateValue: string | null;
  amount: string;
  vatAmount: string;
  totalAmount: string;
}

export interface IncomingUpdLineInput {
  nomenclatureId?: string | null;
  name: string;
  unitSymbol: string;
  quantity: string;
  price: string;
  vatRateValue?: string | null;
}

export interface IncomingUpdOrganizationRef {
  id: string;
  shortName: string;
}

export interface IncomingUpdCounterpartyRef {
  id: string;
  shortName: string;
  inn: string | null;
}

export interface IncomingUpd {
  id: string;
  organizationId: string;
  organization: IncomingUpdOrganizationRef;
  counterpartyId: string;
  counterparty: IncomingUpdCounterpartyRef;
  number: string;
  date: string;
  subtotal: string;
  vatAmount: string;
  total: string;
  status: IncomingDocumentStatus;
  receivedAt: string;
  decidedAt: string | null;
  rejectReason: string | null;
  comment: string | null;
  lines: IncomingUpdLine[];
}

export interface IncomingUpdInput {
  organizationId: string;
  counterpartyId: string;
  number: string;
  date: string;
  comment?: string | null;
  lines: IncomingUpdLineInput[];
}

export interface IncomingUpdListItem {
  id: string;
  organizationId: string;
  organization: IncomingUpdOrganizationRef;
  counterpartyId: string;
  counterparty: IncomingUpdCounterpartyRef;
  number: string;
  date: string;
  total: string;
  status: IncomingDocumentStatus;
  receivedAt: string;
}

export interface IncomingUpdListResponse {
  items: IncomingUpdListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface IncomingUpdListParams {
  search?: string;
  organizationId?: string;
  counterpartyId?: string;
  status?: IncomingDocumentStatus;
  page?: number;
  pageSize?: number;
}

function buildQuery(params: IncomingUpdListParams): string {
  const search = new URLSearchParams();
  if (params.search) search.set('search', params.search);
  if (params.organizationId) search.set('organizationId', params.organizationId);
  if (params.counterpartyId) search.set('counterpartyId', params.counterpartyId);
  if (params.status) search.set('status', params.status);
  if (params.page !== undefined) search.set('page', String(params.page));
  if (params.pageSize !== undefined) search.set('pageSize', String(params.pageSize));
  const query = search.toString();
  return query ? `?${query}` : '';
}

export const incomingUpdsApi = {
  list: (params: IncomingUpdListParams = {}) =>
    api.get<IncomingUpdListResponse>(`/incoming-upds${buildQuery(params)}`),

  get: (id: string) => api.get<{ incomingUpd: IncomingUpd }>(`/incoming-upds/${id}`),

  create: (data: IncomingUpdInput) => api.post<{ incomingUpd: IncomingUpd }>('/incoming-upds', data),

  update: (id: string, data: IncomingUpdInput) =>
    api.patch<{ incomingUpd: IncomingUpd }>(`/incoming-upds/${id}`, data),

  remove: (id: string) => api.delete<void>(`/incoming-upds/${id}`),

  accept: (id: string) => api.post<{ incomingUpd: IncomingUpd }>(`/incoming-upds/${id}/accept`),

  reject: (id: string, reason: string) =>
    api.post<{ incomingUpd: IncomingUpd }>(`/incoming-upds/${id}/reject`, { reason }),
};
